import { errorCode } from '@sd/client-core';
import { LIMITS } from '@sd/contracts';

/**
 * Korean copy for error codes: one wording per code on every screen, dialog and toast.
 * Where a code means something narrower (leaving a group, adding people), the call site
 * passes `overrides`.
 */
const MESSAGES: Record<string, string> = {
  UNAUTHENTICATED: '로그인이 필요해요. 다시 로그인해 주세요',
  TOKEN_EXPIRED: '로그인 시간이 지났어요. 다시 로그인해 주세요',
  SESSION_REVOKED: '이 기기의 로그인이 해제됐어요. 다시 로그인해 주세요',
  INVALID_CREDENTIALS: '이메일 또는 비밀번호가 맞지 않아요',
  MFA_INVALID: '인증 코드가 맞지 않아요. 다시 입력해 주세요',
  CSRF_FAILED: '보안 확인에 실패했어요. 페이지를 새로고침한 뒤 다시 시도해 주세요',
  FORBIDDEN: '이 작업을 할 권한이 없어요',
  ACCOUNT_DEACTIVATED: '비활성화된 계정이에요. 회사 관리자에게 문의해 주세요',
  NOT_FOUND: '찾을 수 없어요. 이미 바뀌었거나 삭제됐을 수 있어요',
  CONFLICT: '다른 곳에서 먼저 바뀌었어요. 새로고침한 뒤 다시 시도해 주세요',
  REVISION_CONFLICT: '다른 곳에서 먼저 바뀌었어요. 최신 내용을 확인한 뒤 다시 시도해 주세요',
  LAST_OWNER: '마지막 소유자는 바꾸거나 비활성화할 수 없어요. 먼저 소유권을 넘겨 주세요',
  ALREADY_MEMBER: '이미 가입된 이메일이에요',
  EMAIL_TAKEN: '이미 가입된 이메일이에요',
  INVITE_INVALID: '초대 링크가 올바르지 않아요',
  INVITE_EXPIRED: '초대가 만료됐어요',
  INVITE_REVOKED: '취소된 초대예요',
  INVITE_ALREADY_ACCEPTED: '이미 수락한 초대예요',
  VALIDATION_FAILED: '입력한 내용을 다시 확인해 주세요',
  RATE_LIMITED: '요청이 너무 많아요. 잠시 후 다시 시도해 주세요',
  UNSUPPORTED_CLIENT: '이 앱 버전은 회사 서버에서 지원하지 않아요. 앱을 업데이트해 주세요',
  MAINTENANCE: '서버 점검 중이에요. 잠시 후 다시 시도해 주세요',
  SETUP_REQUIRED: '회사 서버 초기 설정이 아직 끝나지 않았어요',
  INTERNAL: '서버에 문제가 생겼어요. 잠시 후 다시 시도해 주세요',
  NETWORK: '서버에 연결할 수 없어요. 네트워크 상태를 확인해 주세요',
  TLS: '보안 연결(HTTPS)을 확인할 수 없어요. 회사 관리자에게 문의해 주세요',
  EDIT_WINDOW_EXPIRED: '보낸 지 오래된 메시지는 수정하거나 삭제할 수 없어요',
  READ_ONLY_CONVERSATION: '읽기 전용 대화라 보낼 수 없어요',
  FILE_NOT_READY: '검사가 끝나면 다운로드할 수 있어요',
  FILE_INFECTED: '악성코드가 발견된 파일이에요',
  FILE_TOO_LARGE: '파일이 너무 커요',
  FILE_TYPE_NOT_ALLOWED: '실행 파일이거나 이름과 내용이 다른 파일이라 보낼 수 없어요',
  TOO_MANY_UPLOADS: '동시에 올리는 파일이 많아요. 잠시 후 다시 시도해 주세요',
  TOO_MANY_REACTIONS: `한 메시지에는 반응을 ${LIMITS.maxReactionsPerMessage}가지까지 남길 수 있어요`,
  STORAGE_FULL: '회사 서버의 저장 공간이 부족해요. 회사 관리자에게 알려 주세요',
  IDEMPOTENCY_MISMATCH: '같은 메시지가 다른 내용으로 이미 저장돼 있어요. 이 메시지는 지우고 다시 보내 주세요',
  STALE: '오래 기다린 메시지예요. 다시 보낼지 확인해 주세요',
  ATTACH_AGAIN: '파일을 다시 첨부해 주세요',
};

const FALLBACK = '요청을 처리하지 못했어요. 잠시 후 다시 시도해 주세요';

/** Copy for an error thrown by the API client or bridge, or for an error code. */
export function errorMessage(error: unknown, overrides: Readonly<Record<string, string>> = {}): string {
  const code = typeof error === 'string' ? error : error == null ? null : errorCode(error);
  return (code && (overrides[code] ?? MESSAGES[code])) || FALLBACK;
}
